<template>
  <div class="consent-report-container">
    <!-- Control Bar -->
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Student Consent Form Report</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ reportItems.length }} Records
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">Loading consent records...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" @click="loadReportData" class="mt-3"
        >Retry</b-button
      >
    </div>

    <!-- Report Content -->
    <div v-else class="report-page">
      <!-- Header -->
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div class="logo-wrapper mr-3" v-if="schoolLogo">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName
              }}<span v-if="campusBranch" class="text-muted">
                - {{ campusBranch }}</span
              >
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">Consent Form Report</span>
              <span class="date-range-text">
                <strong>Generated on {{ currentDateTime }}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Filters Summary (Optional) -->
      <div class="report-filters mb-3 no-print" v-if="hasFilters">
        <span v-if="searchTerm" class="mr-2"
          ><strong>Search:</strong> {{ searchTerm }}</span
        >
        <span v-if="status" class="mr-2"
          ><strong>Status:</strong> {{ status }}</span
        >
      </div>

      <!-- Data Table -->
      <div class="table-container">
        <table class="report-table">
          <thead>
            <tr>
              <th class="text-center" style="width: 5%">#</th>
              <th class="text-left" style="width: 20%">CONSENT TITLE</th>
              <th class="text-left" style="width: 18%">STUDENT NAME</th>
              <th class="text-center" style="width: 10%">GR. NO</th>
              <th class="text-center" style="width: 10%">CLASS/SEC</th>
              <th class="text-center" style="width: 12%">PHONE NO</th>
              <th class="text-center" style="width: 10%">STATUS</th>
              <th class="text-left" style="width: 15%">CAMPUS</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in reportItems" :key="index">
              <td class="text-center font-weight-bold">{{ index + 1 }}</td>
              <td class="text-left">{{ item.title || "-" }}</td>
              <td class="text-left font-weight-bold">
                {{ item.studentName || "-" }}
              </td>
              <td class="text-center gr-cell">{{ item.grNo || "-" }}</td>
              <td class="text-center">
                {{ item.clsName || "-" }} / {{ item.sec || "-" }}
              </td>
              <td class="text-center">{{ item.phoneNumber || "-" }}</td>
              <td class="text-center">
                <span
                  :class="
                    'status-text ' + (item.status || 'pending').toLowerCase()
                  "
                >
                  {{ (item.status || "Pending").toUpperCase() }}
                </span>
              </td>
              <td class="text-left small">{{ item.metaCampusName || "-" }}</td>
            </tr>
            <tr v-if="reportItems.length === 0">
              <td colspan="8" class="text-center py-5 no-data">
                <feather-icon
                  icon="InboxIcon"
                  size="32"
                  class="text-muted mb-2"
                />
                <p>No consent records found for the selected criteria</p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Footer -->
      <div class="page-footer">
        <small
          >Powered by MySkool.app | Generated On: {{ currentDateTime }}</small
        >
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

export default {
  name: "ConsentPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      reportItems: [],
      campusName: "MY SKOOL",
      campusBranch: "",
      schoolLogo: null,
      db: "",
      cID: "",
      searchTerm: "",
      classID: "",
      status: "",
      fromDate: "",
      toDate: "",
      reportDomain: (this.$store.state.report || "").replace(/_/g, ""),
    };
  },
  computed: {
    currentDateTime() {
      const now = new Date();
      return now.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    hasFilters() {
      return this.searchTerm || this.status || this.classID;
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const query = this.$route.query;
      this.db = query.db;
      this.cID = query.cId || 0;
      this.searchTerm = query.searchTerm || "";
      this.classID = query.classID || "";
      this.status = query.status || "";
      this.fromDate = query.fromDate || "";
      this.toDate = query.toDate || "";

      if (!this.db) {
        this.error =
          "Database identifier is missing. Please close and try again.";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        const url = `https://apiv26.myskool.app/api/campuses`;
        const response = await axios.get(url, {
          params: { db: this.db, cId: this.cID },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          if (match) {
            this.campusName = match.name;
            this.campusBranch = this.cID == 0 ? "All Campuses" : match.branch;
            this.schoolLogo = match.logo;
          }
        }
      } catch (e) {
        console.warn("Campus info fetch failed");
      }
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;

      try {
        const url = `${this.$store.state.domain}Consents/GetAllConsents`;

        const params = {
          db: this.db,
          campusID: this.cID,
          searchTerm: this.searchTerm,
          classID: this.classID,
          status: this.status,
          fromDate: this.fromDate,
          toDate: this.toDate,
          page: 1,
          pageSize: 10000,
        };

        const response = await axios.get(url, {
          params,
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (response.data && response.data.status === "success") {
          const responseData = response.data.data;
          if (responseData && Array.isArray(responseData.data)) {
            this.reportItems = responseData.data;
          } else {
            this.reportItems = responseData || [];
          }
        } else {
          this.error =
            response.data?.message || "Failed to retrieve consent data.";
        }
      } catch (err) {
        console.error("Error fetching consent report:", err);
        this.error =
          "Failed to retrieve report data. Please ensure the API is running.";
      } finally {
        this.loading = false;
      }
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.consent-report-container {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1rem 2rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.2rem;
  }
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.btn-modern {
  display: flex;
  align-items: center;
  padding: 8px 20px;
  border-radius: 8px;
  font-weight: 700;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(108, 92, 231, 0.3);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;
  &:hover {
    color: #e74c3c;
    border-color: #fab1a0;
  }
}

.report-page {
  background: white;
  padding: 3rem;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.05);
  max-width: 1200px;
  margin: 0 auto;
}

.report-header-modern {
  border-bottom: 3px solid #000;
  padding-bottom: 20px;
  .school-logo-img {
    height: 90px;
    width: auto;
    object-fit: contain;
  }
  .school-name-full {
    font-size: 1.8rem;
    font-weight: 800;
    color: #000;
  }
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1.5rem;

  th,
  td {
    padding: 10px 12px;
    border: 1px solid #dee2e6;
    font-size: 0.9rem;
  }

  thead th {
    background: #2c3e50;
    color: white;
    font-weight: 700;
    text-transform: uppercase;
  }

  tbody tr:nth-child(even) {
    background-color: #f8f9fa;
  }

  .gr-cell {
    font-weight: 700;
    color: #6c5ce7;
  }

  .status-text {
    font-weight: 700;
    font-size: 0.8rem;
    padding: 2px 8px;
    border-radius: 4px;
    &.accepted {
      color: #27ae60;
    }
    &.rejected {
      color: #e74c3c;
    }
    &.pending {
      color: #f39c12;
    }
  }
}

.page-footer {
  text-align: center;
  margin-top: 2rem;
  color: #95a5a6;
  border-top: 1px solid #eee;
  padding-top: 1rem;
}

@media print {
  .no-print {
    display: none !important;
  }
  body {
    background: white !important;
  }
  .consent-report-container {
    padding: 0 !important;
    background: white !important;
  }
  .report-page {
    box-shadow: none !important;
    padding: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
  }
  .report-table {
    font-size: 8pt !important;
    thead th {
      background: #eee !important;
      color: black !important;
    }
  }
}
</style>
